// src/components/privacyNotice/PrivacyNotice.tsx
import React, { FC, useState, useRef, useEffect } from 'react'
import { cn } from '@src/utils/cn.ts'

export type PrivacyNoticeProps = {
  show?: boolean
  className?: string
  titleId?: string
}

export const PrivacyNotice: FC<PrivacyNoticeProps> = ({ show = true, className, titleId = 'privacy-dialog-title' }) => {
  const [showDetails, setShowDetails] = useState(false)
  const detailsRef = useRef<HTMLDetailsElement | null>(null)

  useEffect(() => {
    if (detailsRef.current) setShowDetails(detailsRef.current.open)
  }, [])

  if (!show) return null

  return (
    <div
      className={cn('privacyNotice', className)}
      aria-labelledby={titleId}
      role="region"
    >
      {!showDetails && (
        <p
          style={{ textAlign: 'justify', lineHeight: '24px', marginBottom: 12 }}
          id={titleId}
        >
          <strong>Notice of Collection:</strong> Personal information is collected by your organization through this app to deliver and support educational programs and related services. We handle
          this information only on your organization’s instructions. You may have rights to access or correct your information. For questions, contact your organization’s privacy contact.
        </p>
      )}

      <details
        ref={detailsRef}
        open={showDetails}
        onToggle={(e) => setShowDetails((e.currentTarget as HTMLDetailsElement).open)}
        style={{ display: 'block' }}
        aria-expanded={showDetails}
      >
        <summary
          style={{ display: 'inline', cursor: 'pointer', textDecoration: 'underline', fontWeight: 500 }}
          role="button"
          aria-controls="privacy-details-content"
        >
          {showDetails ? 'Hide details' : 'Details'}
        </summary>
        {showDetails && (
          <div
            id="privacy-details-content"
            style={{ marginTop: 12, whiteSpace: 'pre-line' }}
            role="region"
            aria-label="Detailed privacy notice"
          >
            <strong>Detailed privacy notice</strong>
            <br />
            <strong>Notice of Collection: </strong>Personal information is collected by your organization (the data controller) via this app (QTrobot) for: (i) delivering and supporting educational
            programs and services; (ii) account administration and user support; (iii) ensuring security and preventing misuse; and (iv) service improvement consistent with your organization’s
            policies. LuxAI acts as a service provider/processor and handles information only on your organization’s instructions. Applicable privacy laws may include:
            <br />
            <br />
            <strong>United States: </strong> FERPA, COPPA, and state student-privacy laws (e.g., California SOPIPA/CPRA, New York Education Law § 2-d, Illinois SOPPA).
            <br />
            <strong>United Kingdom: </strong> UK GDPR and the Data Protection Act 2018.
            <br />
            <strong>European Union: </strong> GDPR and applicable Member State laws.
            <br />
            <strong>Canada: </strong> PIPEDA and provincial public-sector privacy laws (e.g., Ontario MFIPPA, British Columbia FIPPA, Alberta FOIP).
            <br />
            <br />
            You may have rights to access, correct, or obtain information about this processing. For questions, contact your organization’s privacy contact.
          </div>
        )}
      </details>
    </div>
  )
}

export default PrivacyNotice
